// IndexedDB wrapper for the offline mail cache. Thin promise layer over the
// raw IDB API so the rest of the code can do `await idbGet(store, key)` etc.
//
// Schema (DB_NAME=mc_webmail_cache, version 1):
//   pages    - keyPath [slot, folder, mode, pageIndex].
//              { slot, folder, mode: 'flat'|'threaded', pageIndex,
//                items: [...],        // raw API items (threads or messages)
//                total, nextCursor, hasMore, cachedAt }
//   bodies   - keyPath [slot, folder, uid].  Folder is REQUIRED: IMAP UIDs are
//              per-folder, so [slot, uid] alone collides across folders and
//              serves one message's body under another's subject.
//              { slot, uid, body, ...detailResponse, cachedAt }
//              Index by 'cachedAt' so we can evict LRU bodies.
//   folders  - keyPath [slot, folder].
//              { slot, folder, mode, lastAccessedAt }
//              Used for LRU eviction of cached folders.

const DB_NAME    = "mc_webmail_cache";
// v2: purge the `bodies` store on upgrade. Cached message detail from older
// builds could carry a stale/incorrect `attachments` list (e.g. a body part
// listed as an attachment, or a uid whose mailbox was resynced), which painted
// a phantom attachment chip whose download 404s. Dropping the store forces a
// fresh fetch of each body on next open; pages/folders caches are untouched.
const DB_VERSION = 3;

let dbPromise = null;

// Close the open connection so a deleteDatabase() (e.g. from Force reload)
// isn't blocked by it - a blocked delete is what can wedge IndexedDB and
// stall every later cache read.
export async function closeDb() {
  if (!dbPromise) return;
  const p = dbPromise;
  dbPromise = null;
  try { const db = await p; db.close?.(); } catch { /* already gone */ }
}

export function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (!("indexedDB" in self)) {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    // Settle exactly once. CRITICAL: an indexedDB.open() can hang forever
    // (a pending deleteDatabase, a version-change blocked by another tab,
    // a wedged profile) and would otherwise stall every cache read -
    // freezing the message list before it ever hits the network. A timeout
    // rejects so callers fall through to the network instead of spinning.
    let settled = false;
    const finish = (fn, val) => { if (!settled) { settled = true; fn(val); } };
    const timer = setTimeout(
      () => finish(reject, new Error("IndexedDB open timed out")),
      2500,
    );

    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("pages")) {
        db.createObjectStore("pages", { keyPath: ["slot", "folder", "mode", "pageIndex"] });
      }
      // Recreate `bodies` so any stale detail caches (with bad attachment
      // lists) from a previous version are dropped. Safe on first install too:
      // the store simply doesn't exist yet.
      if (db.objectStoreNames.contains("bodies")) db.deleteObjectStore("bodies");
      const s = db.createObjectStore("bodies", { keyPath: ["slot", "folder", "uid"] });
      s.createIndex("cachedAt", "cachedAt");
      s.createIndex("slot", "slot");
      if (!db.objectStoreNames.contains("folders")) {
        db.createObjectStore("folders", { keyPath: ["slot", "folder"] });
      }
    };
    req.onsuccess = () => { clearTimeout(timer); finish(resolve, req.result); };
    req.onerror   = () => { clearTimeout(timer); finish(reject, req.error ?? new Error("IndexedDB open failed")); };
    req.onblocked = () => { clearTimeout(timer); finish(reject, new Error("IndexedDB upgrade blocked by another tab")); };
  });
  // If opening fails, clear the cached promise so a later attempt can retry.
  dbPromise.catch(() => { dbPromise = null; });
  return dbPromise;
}

function wrap(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error ?? new Error("IDB op failed"));
  });
}

function awaitTx(t) {
  return new Promise((resolve, reject) => {
    t.oncomplete = () => resolve();
    t.onerror    = () => reject(t.error ?? new Error("IDB tx failed"));
    t.onabort    = () => reject(t.error ?? new Error("IDB tx aborted"));
  });
}

export async function idbGet(storeName, key) {
  const db = await openDb();
  const t = db.transaction([storeName], "readonly");
  return wrap(t.objectStore(storeName).get(key));
}

export async function idbPut(storeName, value) {
  const db = await openDb();
  const t = db.transaction([storeName], "readwrite");
  t.objectStore(storeName).put(value);
  return awaitTx(t);
}

export async function idbDelete(storeName, key) {
  const db = await openDb();
  const t = db.transaction([storeName], "readwrite");
  t.objectStore(storeName).delete(key);
  return awaitTx(t);
}

export async function idbGetAll(storeName) {
  const db = await openDb();
  const t = db.transaction([storeName], "readonly");
  return wrap(t.objectStore(storeName).getAll());
}

// Count records - used to decide when to evict.
export async function idbCount(storeName) {
  const db = await openDb();
  const t = db.transaction([storeName], "readonly");
  return wrap(t.objectStore(storeName).count());
}

// Evict the N oldest records (by index) from a store. Stops after maxDeletes.
export async function idbEvictByIndex(storeName, indexName, maxDeletes) {
  if (maxDeletes <= 0) return 0;
  const db = await openDb();
  const t = db.transaction([storeName], "readwrite");
  const idx = t.objectStore(storeName).index(indexName);
  return new Promise((resolve, reject) => {
    let deleted = 0;
    const req = idx.openCursor(null, "next"); // oldest first
    req.onsuccess = () => {
      const c = req.result;
      if (!c || deleted >= maxDeletes) { resolve(deleted); return; }
      c.delete();
      deleted += 1;
      c.continue();
    };
    req.onerror = () => reject(req.error ?? new Error("IDB evict failed"));
  });
}

// Delete every record in a store whose first key component equals `slot`.
// Used on sign-out for a slot.
export async function idbDeleteBySlot(storeName, slot) {
  const db = await openDb();
  const t = db.transaction([storeName], "readwrite");
  const store = t.objectStore(storeName);
  // Compound-keyPath ranges: lower=[slot, -∞], upper=[slot, +∞] using empty
  // string and the magic high sentinel "uffff" doesn't work generically.
  // Walk a cursor with a key range bounded by [slot] on the first component.
  // IDBKeyRange.bound on compound keys requires same-arity bounds; we use
  // [slot] and [slot, []] (an array sorts after any primitive) per the spec.
  const range = IDBKeyRange.bound([slot], [slot, []], false, false);
  return new Promise((resolve, reject) => {
    const req = store.openCursor(range);
    req.onsuccess = () => {
      const c = req.result;
      if (!c) { resolve(); return; }
      c.delete();
      c.continue();
    };
    req.onerror = () => reject(req.error ?? new Error("IDB sweep failed"));
  });
}
